import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs, { type Dayjs } from "dayjs";

export interface NextUnwrittenEncounter {
  encounterId: string | null;
  patientId: string | null;
  customerId: string | null;
  observationDateTime: string | null;
  patientAge: number | null;
  patientSex: string | null;
  observationDatetime: Dayjs;
}

const QUERY_KEY = "next-unwritten-encounter";

async function fetchNextUnwrittenEncounter({
  customerIds,
  fromDate,
  excludeIds,
}: {
  customerIds: string[];
  fromDate?: string | null;
  excludeIds?: string[];
}): Promise<NextUnwrittenEncounter | null> {
  // Query to fetch the next encounter with approved results that haven't been processed
  let query = supabase
    .from("encounter_with_approved_result_unwritten")
    .select("encounter_id, patient_id, customer_id, observation_datetime, patient_age, patient_sex")
    .in("customer_id", customerIds);

  if (fromDate) {
    query = query.gte("observation_datetime", fromDate);
  }

  // Apply excludeIds filter only if there are IDs to exclude
  if (excludeIds && excludeIds.length > 0) {
    const normalizedExcludeIds = Array.from(new Set(excludeIds.filter(Boolean)));
    if (normalizedExcludeIds.length > 0) {
      // Supabase .not() with 'in' for UUID columns requires double-quoted UUIDs
      const formattedIds = normalizedExcludeIds.map((id) => `"${id}"`).join(",");
      query = query.not("encounter_id", "in", `(${formattedIds})`);
    }
  }

  const { data: nextEncounter, error: nextEncounterError } = await query
    .order("observation_datetime", { ascending: true })
    .limit(1)
    .maybeSingle();

  if (nextEncounterError) {
    console.error("Error fetching next unwritten encounter", nextEncounterError);
    throw nextEncounterError;
  }

  if (nextEncounter) {
    return {
      encounterId: nextEncounter.encounter_id,
      patientId: nextEncounter.patient_id,
      customerId: nextEncounter.customer_id,
      observationDateTime: nextEncounter.observation_datetime,
      patientAge: nextEncounter.patient_age,
      patientSex: nextEncounter.patient_sex,
      observationDatetime: dayjs(nextEncounter.observation_datetime),
    };
  }

  return null;
}

export function useNextUnwrittenEncounter({
  enabled,
  customerIds,
  fromDate,
  excludeIds,
}: {
  customerIds: string[];
  enabled?: boolean;
  fromDate?: string | null;
  excludeIds?: string[];
}) {
  const queryClient = useQueryClient();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [QUERY_KEY, customerIds.join(","), fromDate, excludeIds?.join(",")],
    refetchInterval: (query) => (query.state.data === null ? 30000 : false),
    queryFn: () => fetchNextUnwrittenEncounter({ customerIds, fromDate, excludeIds }),
    enabled: enabled ?? true,
    staleTime: 0, // Always fetch fresh data
    gcTime: 0, // Don't cache results
  });

  // Function to refetch with custom excludeIds
  const refetchWithExcludeIds = async (customExcludeIds: string[]) => {
    return await queryClient.fetchQuery({
      queryKey: [QUERY_KEY, customerIds.join(","), fromDate, customExcludeIds.join(",")],
      queryFn: () =>
        fetchNextUnwrittenEncounter({
          customerIds,
          fromDate,
          excludeIds: customExcludeIds,
        }),
      staleTime: 0,
    });
  };

  return {
    data,
    isLoading,
    error,
    refetch,
    refetchWithExcludeIds,
  };
}
